import React, { useEffect, useState } from 'react';
import { getCurrencyDisplay } from '../utils/currency';

const BACKEND = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

export default function PayBillPage() {
  const billId = window.location.pathname.split('/').pop();
  const [bill, setBill] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');

  const [confirming, setConfirming] = useState(false); // true while polling after redirect
const [pendingManual, setPendingManual] = useState(false); // gave up polling, not yet confirmed
const [paymentFailed, setPaymentFailed] = useState(false); // gateway webhook confirmed a failure

useEffect(() => {
  if (!billId) return;
  const params = new URLSearchParams(window.location.search);
  const justReturned = params.get('paid') === '1';

  const fetchBill = () => fetch(`${BACKEND}/payments/bill/${billId}`).then(r => r.json());

  if (!justReturned) {
    fetchBill()
      .then(d => { setBill(d); setLoading(false); })
      .catch(() => { setError('Bill not found'); setLoading(false); });
    return;
  }

  // Returned from a gateway redirect — never trust the query param alone.
  // Poll the backend a few times, since some gateways' webhooks lag a
  // couple of seconds behind the browser redirect.
  setConfirming(true);
  let attempts = 0;
  const maxAttempts = 6; // ~15s total
  const poll = () => {
    fetchBill()
      .then(d => {
        attempts++;
        setBill(d);
        setLoading(false);
        if (d?.status === 'paid') {
          setConfirming(false);
          return;
        }
        // A gateway with a real webhook (Stripe, Cashfree) can report a
        // definite failure — no need to keep polling or show "pending".
        if (d?.payment_status === 'failed') {
          setConfirming(false);
          setError('');
          setPendingManual(false);
          setPaymentFailed(true);
          return;
        }
        if (attempts >= maxAttempts) {
          setConfirming(false);
          setPendingManual(true);
          return;
        }
        setTimeout(poll, 2500);
      })
      .catch(() => {
        attempts++;
        if (attempts >= maxAttempts) {
          setConfirming(false);
          setError('Could not confirm payment status. Please contact the merchant.');
          setLoading(false);
          return;
        }
        setTimeout(poll, 2500);
      });
  };
  poll();
}, []);

  const handlePay = async () => {
    if (!bill?.payment_link) return;
    setPaying(true);
    window.location.href = bill.payment_link;
  };

  if (loading) return (
<div style={{ minHeight:'100vh', background:'#0f172a', display:'flex', alignItems:'center', justifyContent:'center', color:'#94a3b8', fontFamily:'system-ui' }}>
      Loading...
</div>
  );
if (confirming) return (
<div style={{ minHeight:'100vh', background:'#0f172a', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'system-ui', padding:24 }}>
<div style={{ background:'#1e293b', border:'2px solid #f59e0b', borderRadius:24, padding:48, textAlign:'center', maxWidth:400 }}>
<div style={{ width:48, height:48, border:'4px solid #334155', borderTopColor:'#f59e0b', borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto' }} />
<div style={{ fontSize:20, fontWeight:900, color:'#fbbf24', marginTop:20 }}>Confirming your payment...</div>
<div style={{ fontSize:13, color:'#94a3b8', marginTop:8 }}>This usually takes a few seconds. Please don't close this page.</div>
</div>
<style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
</div>
  );
if (pendingManual) return (
<div style={{ minHeight:'100vh', background:'#0f172a', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'system-ui', padding:24 }}>
<div style={{ background:'#1e293b', border:'2px solid #f59e0b', borderRadius:24, padding:48, textAlign:'center', maxWidth:420 }}>
<div style={{ fontSize:56 }}>⏳</div>
<div style={{ fontSize:22, fontWeight:900, color:'#fbbf24', marginTop:12 }}>Payment Received — Awaiting Confirmation</div>
<div style={{ fontSize:13, color:'#94a3b8', marginTop:10, lineHeight:1.5 }}>
  If you completed the payment, the merchant will confirm it shortly. This bill will update to "Paid" once they do.
</div>
<div style={{ fontSize:11, color:'#64748b', marginTop:16 }}>
  You can safely close this page — no further action is needed from you.
</div>
</div>
</div>
  );
if (paymentFailed) return (
<div style={{ minHeight:'100vh', background:'#0f172a', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'system-ui', padding:24 }}>
<div style={{ background:'#2a0f0f', border:'2px solid #ef4444', borderRadius:24, padding:48, textAlign:'center', maxWidth:400 }}>
<div style={{ fontSize:72 }}>❌</div>
<div style={{ fontSize:26, fontWeight:900, color:'#ef4444', marginTop:16 }}>Payment Failed</div>
<div style={{ fontSize:13, color:'#fca5a5', marginTop:8, lineHeight:1.5 }}>Your payment did not go through. No charge was made. Please try again.</div>
{bill?.payment_link && (
  <button onClick={() => { window.location.href = bill.payment_link; }} style={{
    marginTop:20, width:'100%', padding:'14px', borderRadius:12, border:'none',
    background:'linear-gradient(135deg,#ef4444,#dc2626)', color:'#fff', fontWeight:900, fontSize:14, cursor:'pointer',
  }}>
    Try Again
  </button>
)}
</div>
</div>
  );
if (error) return (
    <div style={{ minHeight:'100vh', background:'#0f172a', display:'flex', alignItems:'center', justifyContent:'center', color:'#ef4444', fontFamily:'system-ui' }}>
      {error}
    </div>
  );

  if (bill?.paid || bill?.status === 'paid') return (
    <div style={{ minHeight:'100vh', background:'#0f172a', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'system-ui' }}>
      <div style={{ background:'#0f2a1a', border:'2px solid #22c55e', borderRadius:24, padding:48, textAlign:'center', maxWidth:400 }}>
        <div style={{ fontSize:72 }}>✅</div>
        <div style={{ fontSize:28, fontWeight:900, color:'#22c55e', marginTop:16 }}>Payment Successful!</div>
        <div style={{ fontSize:14, color:'#86efac', marginTop:8 }}>Thank you for your payment.</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:'100vh', background:'#0f172a', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'system-ui', padding:24 }}>
      <div style={{ background:'#1e293b', borderRadius:24, padding:40, maxWidth:420, width:'100%', textAlign:'center', boxShadow:'0 32px 80px rgba(0,0,0,0.4)' }}>
        <div style={{ fontSize:13, fontWeight:700, color:'#94a3b8', marginBottom:4 }}>BILL {bill?.invoice_number}</div>
        <div style={{ fontSize:16, fontWeight:700, color:'#e2e8f0', marginBottom:16 }}>{bill?.customer_name}</div>
        <div style={{ fontSize:56, fontWeight:900, color:'#f1f5f9', marginBottom:4 }}>
          {getCurrencyDisplay(bill?.currency || 'INR', bill?.currency_display_mode || 'symbol')}{Number(bill?.amount || 0).toFixed(2)}
        </div>
        <div style={{ fontSize:11, color:'#64748b', marginBottom:32 }}>Total Amount Due</div>

        {bill?.payment_link ? (
          <button onClick={handlePay} disabled={paying} style={{
            width:'100%', padding:'16px', borderRadius:12, border:'none',
            background: paying ? '#334155' : 'linear-gradient(135deg,#22c55e,#16a34a)',
            color:'#fff', fontWeight:900, fontSize:16, cursor: paying ? 'default' : 'pointer',
            boxShadow:'0 8px 24px rgba(34,197,94,0.3)'
          }}>
            {paying ? 'Redirecting...' : '💳 Pay Now'}
          </button>
        ) : (
          <div style={{ color:'#ef4444', fontSize:13 }}>Payment gateway not configured for this merchant.</div>
        )}

        <div style={{ marginTop:16, fontSize:11, color:'#475569' }}>
          Secure payment powered by {bill?.gateway_name || 'your merchant'}
        </div>
      </div>
    </div>
  );
}
